import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Bell,
  Mail,
  MessageSquare,
  Settings,
  Save,
  AlertCircle,
  CheckCircle,
  Send,
  FileText,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

export const NotificationsPanel = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    bookingConfirmation: true,
    paymentReceipt: true,
    flightReminder: true,
    reminderDays: 2,
    marketingEmails: false,
    adminAlerts: true
  });

  const [emailTemplates, setEmailTemplates] = useState({
    bookingConfirmation: {
      subject: 'Your Flight Booking Confirmation - {{pnr}}',
      body: `Dear {{passenger_name}},

Thank you for booking with Flight380!

Your booking has been confirmed.

PNR: {{pnr}}
Route: {{origin}} → {{destination}}
Date: {{departure_date}}
Flight: {{airline}} {{flight_number}}

Total Amount: £{{price}}

Please arrive at the airport at least 3 hours before departure for international flights.

Safe travels!
Flight380 Team`
    },
    flightReminder: {
      subject: 'Flight Reminder - Your trip is coming up!',
      body: `Dear {{passenger_name}},

This is a friendly reminder that your flight is in {{days_until}} days!

PNR: {{pnr}}
Route: {{origin}} → {{destination}}
Date: {{departure_date}}
Flight: {{airline}} {{flight_number}}

Don't forget to:
✓ Check-in online 24 hours before departure
✓ Prepare your travel documents
✓ Pack your bags according to airline guidelines

Have a great trip!
Flight380 Team`
    }
  });

  const [activeTemplate, setActiveTemplate] = useState('bookingConfirmation');
  const [testEmail, setTestEmail] = useState('');

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleTemplateChange = (field, value) => {
    setEmailTemplates(prev => ({
      ...prev,
      [activeTemplate]: {
        ...prev[activeTemplate],
        [field]: value
      }
    }));
  };

  const handleSaveSettings = () => {
    // In a real app, this would save to backend
    toast.success('Settings saved successfully!');
  };

  const handleSaveTemplate = () => {
    // In a real app, this would save to backend
    toast.success('Email template saved successfully!');
  };

  const handleSendTestEmail = () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    // In a real app, this would send a test email
    toast.success(`Test email sent to ${testEmail}`);
    setTestEmail('');
  };

  return (
    <div className="space-y-6">
      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-500" />
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email Notifications */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Notifications
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="emailNotifications" className="font-medium">Enable Email Notifications</Label>
                    <p className="text-xs text-gray-500">Send emails to customers</p>
                  </div>
                  <Switch
                    id="emailNotifications"
                    checked={settings.emailNotifications}
                    onCheckedChange={(v) => handleSettingChange('emailNotifications', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="bookingConfirmation" className="font-medium">Booking Confirmation</Label>
                    <p className="text-xs text-gray-500">Send confirmation after booking</p>
                  </div>
                  <Switch
                    id="bookingConfirmation"
                    checked={settings.bookingConfirmation}
                    onCheckedChange={(v) => handleSettingChange('bookingConfirmation', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="paymentReceipt" className="font-medium">Payment Receipt</Label>
                    <p className="text-xs text-gray-500">Send receipt after payment</p>
                  </div>
                  <Switch
                    id="paymentReceipt"
                    checked={settings.paymentReceipt}
                    onCheckedChange={(v) => handleSettingChange('paymentReceipt', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="flightReminder" className="font-medium">Flight Reminder</Label>
                    <p className="text-xs text-gray-500">Send reminder before flight</p>
                  </div>
                  <Switch
                    id="flightReminder"
                    checked={settings.flightReminder}
                    onCheckedChange={(v) => handleSettingChange('flightReminder', v)}
                  />
                </div>

                {settings.flightReminder && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Label className="text-sm">Send reminder</Label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={settings.reminderDays}
                      onChange={(e) => handleSettingChange('reminderDays', parseInt(e.target.value))}
                      className="w-16 h-8"
                    />
                    <span className="text-sm text-gray-600">days before departure</span>
                  </div>
                )}
              </div>
            </div>

            {/* Other Notifications */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Other Notifications
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="smsNotifications" className="font-medium">SMS Notifications</Label>
                    <p className="text-xs text-gray-500">Send SMS to customers (premium)</p>
                  </div>
                  <Switch
                    id="smsNotifications"
                    checked={settings.smsNotifications}
                    onCheckedChange={(v) => handleSettingChange('smsNotifications', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="marketingEmails" className="font-medium">Marketing Emails</Label>
                    <p className="text-xs text-gray-500">Send promotional offers</p>
                  </div>
                  <Switch
                    id="marketingEmails"
                    checked={settings.marketingEmails}
                    onCheckedChange={(v) => handleSettingChange('marketingEmails', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="adminAlerts" className="font-medium">Admin Alerts</Label>
                    <p className="text-xs text-gray-500">Get alerts for new bookings</p>
                  </div>
                  <Switch
                    id="adminAlerts"
                    checked={settings.adminAlerts}
                    onCheckedChange={(v) => handleSettingChange('adminAlerts', v)}
                  />
                </div>
              </div>

              <Button onClick={handleSaveSettings} className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-500" />
            Email Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Template Selector */}
            <div className="flex gap-2">
              <Button
                variant={activeTemplate === 'bookingConfirmation' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTemplate('bookingConfirmation')}
                className={activeTemplate === 'bookingConfirmation' ? 'bg-blue-600' : ''}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Booking Confirmation
              </Button>
              <Button
                variant={activeTemplate === 'flightReminder' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTemplate('flightReminder')}
                className={activeTemplate === 'flightReminder' ? 'bg-blue-600' : ''}
              >
                <Bell className="h-4 w-4 mr-2" />
                Flight Reminder
              </Button>
            </div>

            {/* Template Editor */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
              <div>
                <Label htmlFor="subject" className="text-sm font-medium">Subject Line</Label>
                <Input
                  id="subject"
                  value={emailTemplates[activeTemplate].subject}
                  onChange={(e) => handleTemplateChange('subject', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="body" className="text-sm font-medium">Email Body</Label>
                <Textarea
                  id="body"
                  value={emailTemplates[activeTemplate].body}
                  onChange={(e) => handleTemplateChange('body', e.target.value)}
                  className="mt-1 h-64 font-mono text-sm"
                />
              </div>

              {/* Available Variables */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {['{{passenger_name}}', '{{pnr}}', '{{origin}}', '{{destination}}', '{{departure_date}}', '{{airline}}', '{{flight_number}}', '{{price}}', '{{days_until}}'].map(v => (
                    <code key={v} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs">{v}</code>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button onClick={handleSaveTemplate} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </Button>

                <div className="flex-1 flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email for test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={handleSendTestEmail}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-700">Email Service Information</p>
              <p className="text-sm text-blue-600 mt-1">
                Emails are sent via IONOS SMTP. Make sure your SMTP credentials are configured in the backend environment variables.
                Test emails before going live to ensure deliverability.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPanel;
